import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiBriefcase, FiChevronUp, FiLogOut, FiRepeat, FiSettings, FiShield, FiUser, FiUsers } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { EmailInviteLanguage } from "../../../autogen/bff-api";
import { reachesAccessManagement } from "../../../pages/organizations/current/manage-users/roleAdministration";
import { urls } from "../../../urls";
import { useLoggedInWithOrgContextState } from "../../auth/useLoggedInWithOrgContextState";
import { useLogout } from "../../auth/useLogout";
import { getPreferredLanguage, setPreferredLanguage } from "../../local-storage/language";
import { Explanation } from "../../support/Explanation/Explanation";
import { useChangeOrganizationModal } from "../Navbar/useChangeOrganizationModal";
import { ViewAsRoleSelector } from "./ViewAsRoleSelector";

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

export const SidebarUserMenu = () => {
  const { t } = useTranslation();
  const logout = useLogout();
  const navigate = useNavigate();
  const { me, selectedOrg, organizations } = useLoggedInWithOrgContextState();
  const changeOrganizationModal = useChangeOrganizationModal();
  const hasMultipleOrganizations = organizations.length > 1;
  const isNorwegian = getPreferredLanguage() === EmailInviteLanguage.No;

  const switchLanguage = (language: EmailInviteLanguage) => {
    setPreferredLanguage(language);
    window.location.reload();
  };

  const fullName = `${me.firstName ?? ""} ${me.lastName ?? ""}`.trim();

  return (
    <Box flexShrink={0} borderTop="1px solid" borderColor="smBorder" px="3" py="3">
      {changeOrganizationModal.modal}
      <ViewAsRoleSelector />
      <HStack spacing="1.5" fontSize="sm" fontWeight="semibold" mb="3" px="1">
        <LangOption label="NO" active={isNorwegian} onSelect={() => switchLanguage(EmailInviteLanguage.No)} />
        <Text color="smBorder">/</Text>
        <LangOption label="EN" active={!isNorwegian} onSelect={() => switchLanguage(EmailInviteLanguage.En)} />
      </HStack>

      <Menu placement="top-start" matchWidth>
        <MenuButton
          as={Button}
          variant="ghost"
          w="full"
          h="auto"
          px="2"
          py="2"
          _hover={{ bg: "smBackgroundSecondary" }}
          _active={{ bg: "smBackgroundSecondary" }}
        >
          <HStack spacing="2.5">
            <Flex
              w="36px"
              h="36px"
              rounded="full"
              bg="smPrimarySofter"
              color="smPrimary"
              fontSize="13px"
              fontWeight="bold"
              align="center"
              justify="center"
              flexShrink={0}
            >
              {getInitials(fullName)}
            </Flex>
            <Box flex="1" minW="0" textAlign="left">
              <Text fontSize="sm" fontWeight="semibold" color="smText" noOfLines={1}>
                {fullName}
              </Text>
              <Text fontSize="11px" color="smMuted" noOfLines={1}>
                {selectedOrg.name}
              </Text>
            </Box>
            <Icon as={FiChevronUp} color="smMuted" flexShrink={0} />
          </HStack>
        </MenuButton>
        <MenuList>
          <MenuItem icon={<Icon as={FiUser} />} onClick={() => navigate(urls.v2.profile)}>
            {t("Profile")}
          </MenuItem>
          <MenuItem icon={<Icon as={FiBriefcase} />} onClick={() => navigate(urls.v2.organizationProfile.go(selectedOrg.id))}>
            {selectedOrg.name}
          </MenuItem>
          <MenuItem icon={<Icon as={FiSettings} />} onClick={() => navigate(urls.v2.settings)}>
            {t("Settings")}
          </MenuItem>
          <Explanation
            text={t("Only admins can manage users")}
            enabled={!reachesAccessManagement(selectedOrg)}
            placement="right-end"
          >
            <MenuItem
              icon={<Icon as={FiUsers} />}
              isDisabled={!reachesAccessManagement(selectedOrg)}
              onClick={() => navigate(urls.v2.users)}
            >
              {t("Access management")}
            </MenuItem>
          </Explanation>
          {hasMultipleOrganizations && (
            <MenuItem icon={<Icon as={FiRepeat} />} onClick={() => changeOrganizationModal.show()}>
              {t("Switch organization")}
            </MenuItem>
          )}
          {me.isSystemAdmin && (
            <MenuItem icon={<Icon as={FiShield} />} onClick={() => navigate(urls.admin)}>
              {t("Admin dashboard")}
            </MenuItem>
          )}
          <MenuDivider />
          <MenuItem icon={<Icon as={FiLogOut} />} color="smError" onClick={() => void logout()}>
            {t("Log out")}
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
};

const LangOption = ({ label, active, onSelect }: { label: string; active: boolean; onSelect: () => void }) =>
  active ? (
    <Text color="smPrimary" aria-current="true">
      {label}
    </Text>
  ) : (
    <Box as="button" onClick={onSelect} color="smMuted" _hover={{ color: "smPrimary" }}>
      {label}
    </Box>
  );
